<!--
  @project E-School Web
  @author Vothana CHY
  @date 2026-07-13
-->

<script lang="ts" setup>
// Section 10 — Footer
// Reference: 10. HOME.png
// Layout: main footer (light gray #d4d0ca bg, 3-col) + teal copyright bar

import imgLogo from '~/assets/images/logo-full.png';
import iconFacebook from '~/assets/icons/facbook.svg?url';
import iconYoutube from '~/assets/icons/youtube.svg?url';
import iconTelegram from '~/assets/icons/telegram.svg?url';
import iconLinkedin from '~/assets/icons/linkin.svg?url';
import iconMessenger from '~/assets/icons/messenger.svg?url';
import iconPhone from '~/assets/icons/phone.svg?url';
import iconMsgBox from '~/assets/icons/msg-box.svg?url';

import { CONTACT_INFO } from '~/constants/contact';

const { t } = useI18n();
const formattedPhones = CONTACT_INFO.phones.map((p) => p.display).join(' / ');

const startYear = 2017;
const currentYear = new Date().getFullYear();
</script>

<template>
  <footer class="w-full">
    <!-- Main footer body -->
    <div class="w-full bg-[#d4d0ca] py-14">
      <div class="mx-auto grid max-w-360 grid-cols-1 gap-10 px-6 md:grid-cols-[38%_28%_34%] md:gap-8 md:px-20">
        <!-- Col 1: Logo + Description -->
        <div class="flex flex-col">
          <NuxtLink to="/" class="w-fit">
            <img :src="imgLogo" alt="E-School Logo" class="h-auto w-50 object-contain transition hover:opacity-90" />
          </NuxtLink>
          <p class="mt-4 text-[13.5px] leading-relaxed whitespace-pre-line text-gray-700">
            {{ t('footer.desc') }}
          </p>
        </div>

        <!-- Col 2: Address -->
        <div class="flex flex-col">
          <a :href="CONTACT_INFO.googleMapsUrl" target="_blank" class="group">
            <h3 class="text-[18px] font-bold text-gray-900 transition hover:text-brand-blue">
              {{ t('footer.address') }}
            </h3>
            <div
              class="mt-4 space-y-1.5 text-[13.5px] leading-relaxed whitespace-pre-line text-gray-700 transition group-hover:text-brand-blue"
            >
              {{ t('footer.addressVal') }}
            </div>
          </a>
        </div>

        <!-- Col 3: Contact + Social -->
        <div class="flex flex-col">
          <NuxtLink to="/e-contact" class="group">
            <h3 class="flex items-center gap-1 text-[18px] font-bold text-gray-900 transition hover:text-brand-blue">
              {{ t('footer.contact') }}
              <Icon
                name="lucide:arrow-right"
                class="h-4 w-4 text-brand-blue opacity-0 transition-all duration-300 group-hover:translate-x-1 group-hover:opacity-100"
              />
            </h3>
          </NuxtLink>
          <div class="mt-4 space-y-3">
            <!-- Email -->
            <a
              :href="`mailto:${CONTACT_INFO.email}`"
              class="group flex items-center gap-3 text-gray-700 transition hover:text-brand-blue"
            >
              <img
                :src="iconMsgBox"
                alt="Email"
                class="h-4.25 w-5.75 object-contain transition-transform group-hover:scale-110"
              />
              <span class="text-[13px]">{{ CONTACT_INFO.email }}</span>
            </a>
            <!-- Phone -->
            <NuxtLink
              to="/e-contact"
              class="group flex items-center gap-3 text-gray-700 transition hover:text-brand-blue"
            >
              <img
                :src="iconPhone"
                alt="Phone"
                class="h-5.5 w-5.5 object-contain transition-transform group-hover:scale-110"
              />
              <span class="text-[13px]">{{ formattedPhones }}</span>
            </NuxtLink>
            <!-- Social icons -->
            <div class="flex items-center gap-3 pt-1">
              <!-- Facebook -->
              <a
                :href="CONTACT_INFO.socials.facebook"
                target="_blank"
                class="btn-3d flex h-[34px] w-[34px] items-center justify-center rounded-full"
                style="--btn-3d-shadow: rgba(0, 0, 0, 0.18)"
              >
                <img :src="iconFacebook" alt="Facebook" class="h-full w-full object-contain" />
              </a>
              <!-- YouTube -->
              <a
                :href="CONTACT_INFO.socials.youtube"
                target="_blank"
                class="btn-3d flex h-9 w-9 items-center justify-center rounded-full"
                style="--btn-3d-shadow: rgba(0, 0, 0, 0.18)"
              >
                <img :src="iconYoutube" alt="YouTube" class="h-full w-full object-contain" />
              </a>
              <!-- Telegram -->
              <a
                :href="CONTACT_INFO.socials.telegram"
                target="_blank"
                class="btn-3d flex h-[30px] w-[30px] items-center justify-center rounded-full"
                style="--btn-3d-shadow: rgba(0, 0, 0, 0.18)"
              >
                <img :src="iconTelegram" alt="Telegram" class="h-full w-full object-contain" />
              </a>
              <!-- LinkedIn -->
              <a
                :href="CONTACT_INFO.socials.linkedin"
                target="_blank"
                class="btn-3d flex h-9 w-9 items-center justify-center rounded-full"
                style="--btn-3d-shadow: rgba(0, 0, 0, 0.18)"
              >
                <img :src="iconLinkedin" alt="LinkedIn" class="h-full w-full object-contain" />
              </a>
              <!-- Messenger -->
              <a
                :href="CONTACT_INFO.socials.messenger"
                target="_blank"
                class="btn-3d flex h-[30px] w-[30px] items-center justify-center rounded-full"
                style="--btn-3d-shadow: rgba(0, 0, 0, 0.18)"
              >
                <img :src="iconMessenger" alt="Messenger" class="h-full w-full object-contain" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Copyright bar -->
    <div class="w-full bg-brand-blue py-4">
      <div class="mx-auto flex max-w-360 flex-col items-center justify-between gap-2 px-6 sm:flex-row md:px-20">
        <NuxtLink
          to="/term_and_condition"
          class="text-[13px] text-white/80 underline underline-offset-2 hover:text-white"
        >
          {{ t('footer.terms') }}
        </NuxtLink>
        <span class="text-[13px] text-white/80">{{ t('footer.copyright', { startYear, currentYear }) }}</span>
      </div>
    </div>
  </footer>
</template>
